import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Pause, Play, XCircle, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getPortalInviteBatches, getPortalInviteBatch, getPortalInviteBatchRecipients, portalInviteBatchAction,
} from '../../../api/contact';
import { formatMinutes, formatCount } from '../components/InviteAllDialog';
import { formatMelbourneDateTime } from '../../../utils/melbourneTime';

/**
 * Invite All batches: the list, and one batch's live progress with
 * pause / resume / cancel and (for super admins) approve / reject.
 * CG, Monday 13391839919: "Add a live progress screen showing sent, failed,
 * skipped and remaining invites, with the ability to pause or cancel."
 */
const STATUS_STYLES = {
  pending_approval: 'bg-amber-100 text-amber-800',
  queued: 'bg-blue-100 text-blue-800',
  sending: 'bg-cyan-100 text-cyan-800',
  paused: 'bg-gray-200 text-gray-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-700',
  rejected: 'bg-red-100 text-red-700',
};
const ACTIVE = ['pending_approval', 'queued', 'sending', 'paused'];

const StatusPill = ({ status, label }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-700'}`}>
    {label}
  </span>
);

const when = (iso) => (iso ? formatMelbourneDateTime(iso) : '');

const PortalInvitesPage = () => {
  const { id } = useParams();
  return id ? <BatchDetail id={id} /> : <BatchList />;
};

const BatchList = () => {
  const [batches, setBatches] = useState(null);
  useEffect(() => {
    getPortalInviteBatches()
      .then((res) => setBatches(res.data.results || []))
      .catch(() => { toast.error('Could not load invite batches'); setBatches([]); });
  }, []);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link to="/contacts" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Contacts
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Portal invite batches</h1>
      <p className="text-sm text-gray-500 mb-6">
        Every Invite All run, newest first. Start a new one from Contacts &gt; Invite All to Portal.
      </p>
      {batches === null ? (
        <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
      ) : batches.length === 0 ? (
        <div className="text-gray-500 text-sm">No invite batches yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Batch</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">People</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {batches.map((b) => (
                <tr key={b.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <Link to={`/contacts/portal-invites/${b.id}`} className="text-[#2A9DA8] font-medium hover:underline">
                      #{b.id}
                    </Link>
                    {b.note && <span className="text-gray-500"> · {b.note}</span>}
                  </td>
                  <td className="px-4 py-2"><StatusPill status={b.status} label={b.status_label} /></td>
                  <td className="px-4 py-2 text-right">{formatCount(b.total_recipients)}</td>
                  <td className="px-4 py-2 text-gray-600">{when(b.created_at)}</td>
                  <td className="px-4 py-2 text-gray-600">{b.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RECIPIENT_TABS = [
  ['', 'All'], ['queued', 'Remaining'], ['sent', 'Sent'], ['failed', 'Failed'], ['skipped', 'Skipped'], ['cancelled', 'Cancelled'],
];

const BatchDetail = ({ id }) => {
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('');
  const [page, setPage] = useState(1);
  const [recipients, setRecipients] = useState(null);

  const load = useCallback(() => getPortalInviteBatch(id)
    .then((res) => setBatch(res.data))
    .catch(() => toast.error('Could not load this invite batch')), [id]);

  const loadRecipients = useCallback(() => getPortalInviteBatchRecipients(id, { status: tab || undefined, page })
    .then((res) => setRecipients(res.data))
    .catch(() => setRecipients({ results: [], count: 0, num_pages: 1, page: 1 })), [id, tab, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  // Live progress while the batch is still running.
  const active = batch && ACTIVE.includes(batch.status);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => { load(); loadRecipients(); }, 5000);
    return () => clearInterval(t);
  }, [active, load, loadRecipients]);

  const act = async (action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(action);
    try {
      const res = await portalInviteBatchAction(id, action);
      setBatch(res.data);
      loadRecipients();
    } catch (error) {
      toast.error(error?.response?.data?.error || `Could not ${action} this batch`);
    } finally {
      setBusy('');
    }
  };

  if (!batch) {
    return <div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>;
  }

  const s = batch.stats;
  const done = s.sent + s.failed + s.skipped + s.cancelled;
  const pct = s.total ? Math.round((done / s.total) * 100) : 0;
  const remainingMinutes = Math.ceil(s.queued / (batch.rate_per_minute || 1));

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <Link to="/contacts/portal-invites" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="w-4 h-4" /> All invite batches
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Invite batch #{batch.id}</h1>
          <StatusPill status={batch.status} label={batch.status_label} />
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Created {when(batch.created_at)} by {batch.created_by}
          {batch.approved_by && <> · approved by {batch.approved_by} {when(batch.approved_at)}</>}
          {batch.note && <> · {batch.note}</>}
        </p>
      </div>

      {batch.sending_switched_off && (
        <div className="flex gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          Nothing is sending: portal welcome emails are switched off in Settings &gt; Email.
        </div>
      )}
      {batch.status === 'pending_approval' && (
        <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
          Waiting for a super admin to approve. Nothing has been sent.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {batch.can_approve && (
          <>
            <button disabled={!!busy} onClick={() => act('approve', `Approve sending ${formatCount(s.total)} invites?`)}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#55C5D0] text-white hover:bg-[#45B5C0] disabled:opacity-50">
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
            <button disabled={!!busy} onClick={() => act('reject', 'Reject this batch? Nothing will be sent.')}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <XCircle className="w-4 h-4" /> Reject
            </button>
          </>
        )}
        {['queued', 'sending'].includes(batch.status) && (
          <button disabled={!!busy} onClick={() => act('pause')}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <Pause className="w-4 h-4" /> Pause
          </button>
        )}
        {batch.status === 'paused' && (
          <button disabled={!!busy} onClick={() => act('resume')}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <Play className="w-4 h-4" /> Resume
          </button>
        )}
        {ACTIVE.includes(batch.status) && batch.status !== 'pending_approval' && (
          <button disabled={!!busy} onClick={() => act('cancel', 'Cancel this batch? Invites not yet sent will not go out.')}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-white border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Cancel batch
          </button>
        )}
      </div>

      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium text-gray-800">{formatCount(done)} of {formatCount(s.total)} processed</span>
          {s.queued > 0 && batch.status === 'sending' && (
            <span className="text-gray-500">about {formatMinutes(remainingMinutes)} left at {batch.rate_per_minute}/min</span>
          )}
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full bg-[#55C5D0] transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-5">
          <Stat label="Sent" value={s.sent} />
          <Stat label="Remaining" value={s.queued} />
          <Stat label="Failed" value={s.failed} tone={s.failed ? 'bad' : ''} />
          <Stat label="Skipped" value={s.skipped} />
          <Stat label="Cancelled" value={s.cancelled} />
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">After sending</h2>
        <div className="grid grid-cols-2 sm:grid-cols-7 gap-4">
          <Stat label="Delivered" value={s.delivered} />
          <Stat label="Opened" value={s.opened} />
          <Stat label="Clicked" value={s.clicked} />
          <Stat label="Registered" value={s.registered} tone={s.registered ? 'good' : ''} />
          <Stat label="Bounced" value={s.bounced} tone={s.bounced ? 'bad' : ''} />
          <Stat label="Unsubscribed" value={s.unsubscribed} />
          <Stat label="Spam reports" value={s.complaints} tone={s.complaints ? 'bad' : ''} />
        </div>
      </section>

      {batch.excluded.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Left out when the batch was built</h2>
          <ul className="text-sm divide-y divide-gray-100">
            {batch.excluded.map((row) => (
              <li key={row.reason} className="flex justify-between py-1.5">
                <span className="text-gray-700">{row.label}</span>
                <span className="font-medium">{formatCount(row.count)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="bg-white border border-gray-200 rounded-lg">
        <div className="flex flex-wrap gap-1 px-4 pt-3 border-b border-gray-200">
          {RECIPIENT_TABS.map(([key, label]) => (
            <button key={key || 'all'} onClick={() => { setTab(key); setPage(1); }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === key ? 'border-[#55C5D0] text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(recipients?.results || []).map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-1.5">
                  {r.contact_id
                    ? <Link to={`/contacts/${r.contact_id}`} className="text-gray-900 hover:underline">{r.name}</Link>
                    : r.name}
                </td>
                <td className="px-4 py-1.5 text-gray-600 break-all">{r.email}</td>
                <td className="px-4 py-1.5 capitalize">{r.status}</td>
                <td className="px-4 py-1.5 text-gray-500">{r.status === 'sent' ? when(r.sent_at) : r.reason}</td>
              </tr>
            ))}
            {recipients && recipients.results.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Nobody here.</td></tr>
            )}
          </tbody>
        </table>
        {recipients && recipients.num_pages > 1 && (
          <div className="flex items-center justify-end gap-3 px-4 py-3 text-sm text-gray-600">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-40">Previous</button>
            <span>Page {recipients.page} of {recipients.num_pages}</span>
            <button disabled={page >= recipients.num_pages} onClick={() => setPage(page + 1)} className="disabled:opacity-40">Next</button>
          </div>
        )}
      </section>
    </div>
  );
};

const Stat = ({ label, value, tone }) => (
  <div>
    <div className={`text-2xl font-semibold ${tone === 'bad' ? 'text-red-600' : tone === 'good' ? 'text-green-600' : 'text-gray-900'}`}>
      {formatCount(value || 0)}
    </div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

export default PortalInvitesPage;
